"use client";

import { useState, useEffect, useRef } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase/config";
import { api } from "@/lib/firebase/api-client";
import { intervalleVisible } from "@/lib/utils/intervalle-visible";
import { COLLECTIONS, UserRole } from "@/lib/constants";
import {
  MessageSquare,
  Phone,
  RefreshCw,
  Send,
  AlertTriangle,
  ChevronLeft,
  Tag,
  X,
  Check,
  Pin,
  FileText,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { useAuth } from "@/lib/hooks/useAuth";

interface Store {
  id: string;
  name: string;
  twilioNumber?: string;
  twilioCredentialId?: string;
}

interface Conversation {
  id: string;
  storeId: string;
  contactNumber: string;
  contactName?: string;
  lastMessage?: string;
  lastMessageAt?: any;
  unreadCount: number;
  tags?: string[];
  isPinned?: boolean;
}

// Tags prédéfinis
const AVAILABLE_TAGS = [
  { id: "urgent", label: "🔴 Urgent", color: "#EF4444" },
  { id: "a_traiter", label: "🟡 À traiter", color: "#F59E0B" },
  { id: "en_attente", label: "🟠 En attente", color: "#F97316" },
  { id: "resolu", label: "🟢 Résolu", color: "#22C55E" },
  { id: "important", label: "⭐ Important", color: "#8B5CF6" },
  { id: "rappel", label: "📞 Rappel", color: "#3B82F6" },
];

// Réponses rapides (remplissent la zone de texte sans envoyer)
const QUICK_REPLIES = [
  { label: "Bonjour, aide", text: "Bonjour, je suis là pour vous aider 😊" },
  {
    label: "Désagrément, on vérifie",
    text: "Nous sommes désolés pour ce désagrément. Nous vérifions votre commande et reviendrons vers vous dans quelques minutes 🙏",
  },
  {
    label: "Retard, production + livreur",
    text: "Désolé pour le retard. Nous vérifions avec la production et le livreur et reviendrons vers vous rapidement.",
  },
  {
    label: "Pic de commandes, excuses",
    text: "Navrés d'entendre cela. Nous traversons actuellement un pic de commandes et faisons notre maximum pour vous offrir le meilleur service. Désolés encore pour la gêne occasionnée 🙏",
  },
  {
    label: "Rappel production",
    text: "Un membre de notre équipe production vous appellera sous peu pour régler cela avec vous.",
  },
  {
    label: "Merci compréhension",
    text: "Merci pour votre compréhension ! N'hésitez pas si vous avez d'autres questions 😊",
  },
];

interface Message {
  id: string;
  conversationId: string;
  twilioSid: string;
  direction: "inbound" | "outbound";
  fromNumber: string;
  toNumber: string;
  body: string;
  status?: string;
  sentAt: any;
  hasMedia: boolean;
  mediaUrl?: string;
  mediaType?: string;
  isRead: boolean;
  isAutomatic?: boolean; // 🆕 Message automatique
  // Qui a ecrit. auteurSur vaut false quand c'est une deduction (Amina par
  // elimination) ou quand le message est anterieur a l'enregistrement.
  auteurNom?: string;
  auteurSur?: boolean;
  orderId?: string;
  orderCode?: string;
  isTestMode?: boolean;
}

// Helper pour construire l'URL du média via notre proxy
const getMediaUrl = (
  mediaUrl: string | undefined,
  storeId: string,
): string | null => {
  if (!mediaUrl) return null;

  try {
    // Si c'est un JSON avec messageSid et mediaSid (nouveau format)
    const mediaInfo = JSON.parse(mediaUrl);
    if (mediaInfo.messageSid && mediaInfo.mediaSid) {
      return `/api/messaging/media/${mediaInfo.messageSid}/${mediaInfo.mediaSid}?storeId=${storeId}`;
    }
  } catch {
    // Si ce n'est pas du JSON, c'est peut-être une URL directe (ancien format)
    if (mediaUrl.startsWith("http")) {
      return mediaUrl;
    }
  }

  return null;
};

export default function MessagingPage() {
  // Auth
  const { user } = useAuth();

  // URL params
  const [urlParams] = useState(() => {
    if (typeof window === "undefined") return { conversation: null, phone: null, filter: null };
    const params = new URLSearchParams(window.location.search);
    return {
      conversation: params.get("conversation"),
      phone: params.get("phone"),
      filter: params.get("filter"),
    };
  });

  // State
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStore, setSelectedStore] = useState<string>("");
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] =
    useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");

  // Loading states
  const [loadingStores, setLoadingStores] = useState(true);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [syncingMessages, setSyncingMessages] = useState(false);

  // Credential check
  const [hasCredential, setHasCredential] = useState(false);

  // Mobile view
  const [showMessages, setShowMessages] = useState(false);

  // Tags
  const [showTagMenu, setShowTagMenu] = useState(false);
  const [updatingTags, setUpdatingTags] = useState(false);

  // Réponses rapides
  const [showQuickReplies, setShowQuickReplies] = useState(false);

  // 🔥 Filtre pour messages non lus (activé automatiquement si ?filter=unread)
  const [showUnreadOnly, setShowUnreadOnly] = useState(urlParams.filter === 'unread');
  /** N'afficher que les numeros absents de l'annuaire, a rattacher. */
  const [showInconnusOnly, setShowInconnusOnly] = useState(false);

  // 🆕 Nouvelle conversation (saisie manuelle d'un numéro par le responsable)
  const [showNewConvModal, setShowNewConvModal] = useState(false);
  const [newConvPhone, setNewConvPhone] = useState("");
  const [newConvName, setNewConvName] = useState("");
  const [newConvOrderCode, setNewConvOrderCode] = useState("");
  const [sendingNewConv, setSendingNewConv] = useState(false);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const previousMessageIdsRef = useRef<Set<string>>(new Set());
  const isInitialLoadRef = useRef(true);
  const lastMessageAtRef = useRef<Date | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previousUnreadCountRef = useRef<number>(-1); // -1 = pas encore initialisé

  // Notification sound & permissions
  useEffect(() => {
    // Créer l'élément audio
    audioRef.current = new Audio("/sounds/notification.mp3");
    audioRef.current.volume = 0.5;

    // ❌ NE PLUS demander la permission ici - géré par NotificationPrompt component
    // Firebase Messaging gère tout
  }, []);

  const playNotificationSound = () => {
    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch((e) => {
          console.log("Autoplay blocked:", e);
        });
      }
    } catch (e) {
      console.error("Audio error:", e);
    }
  };

  // Load stores
  useEffect(() => {
    const q = query(
      collection(db, COLLECTIONS.STORES),
      where("isActive", "==", true),
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const storesData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Store[];
      setStores(storesData);
      setLoadingStores(false);
    });

    return () => unsubscribe();
  }, []);

  // Verifie le magasin selectionne, sans attendre qu'une conversation soit
  // ouverte. Sans cela, l'ecran affirmait « Credential Twilio non
  // configuree » alors que le numero existe — et desactivait l'envoi.
  useEffect(() => {
    if (selectedConversation) return;
    if (!selectedStore || selectedStore.startsWith("cat:")) {
      setHasCredential(false);
      return;
    }
    api.messaging
      .checkConfig(selectedStore)
      .then((r) => setHasCredential(!!r.isConfigured))
      .catch(() => setHasCredential(false));
  }, [selectedStore, selectedConversation]);

  // Check config when conversation changes (use conversation's storeId)
  useEffect(() => {
    if (!selectedConversation) {
      return;
    }

    const checkConfig = async () => {
      try {
        const result = await api.messaging.checkConfig(
          selectedConversation.storeId,
        );
        setHasCredential(result.isConfigured);
      } catch (error) {
        console.error("Error checking config:", error);
        setHasCredential(false);
      }
    };

    checkConfig();
  }, [selectedConversation?.storeId]);

  // Load conversations when store changes (real-time) - seulement 3 derniers jours
  useEffect(() => {
    setLoadingConversations(true);
    setSelectedConversation(null);
    setMessages([]);

    // Date limite : 3 jours en arrière (Timestamp Firestore)
    const threeDaysAgo = Timestamp.fromDate(
      new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    );

    let q;
    if (selectedStore.startsWith("cat:")) {
      // Une categorie : fournisseurs, collaborateurs ou equipe interne.
      // Le magasin n'entre pas dans la requete — c'est la categorie qui
      // regroupe, et le filtrage par magasin se fait juste apres, sur le
      // resultat, pour un compte support restreint.
      q = query(
        collection(db, COLLECTIONS.CONVERSATIONS),
        where("categorie", "==", selectedStore.slice(4)),
        where("isActive", "==", true),
        where("lastMessageAt", ">=", threeDaysAgo),
        orderBy("lastMessageAt", "desc"),
      );
    } else if (selectedStore) {
      // Store spécifique
      q = query(
        collection(db, COLLECTIONS.CONVERSATIONS),
        where("storeId", "==", selectedStore),
        where("isActive", "==", true),
        where("lastMessageAt", ">=", threeDaysAgo),
        orderBy("lastMessageAt", "desc"),
      );
    } else {
      // TOUS LES STORES
      q = query(
        collection(db, COLLECTIONS.CONVERSATIONS),
        where("isActive", "==", true),
        where("lastMessageAt", ">=", threeDaysAgo),
        orderBy("lastMessageAt", "desc"),
      );
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        // Les echanges du patron avec Amina sont prives : jamais dans la
        // liste, pour personne. Le fil lui-meme est refuse par le serveur.
        let conversationsData = snapshot.docs
          .filter(
            (doc) =>
              !String(doc.data()?.contactNumber || "")
                .replace(/[^\d]/g, "")
                .endsWith("607072606"),
          )
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as Conversation[];

        // Un compte support ne voit que les fournisseurs de son magasin.
        // Un support sans magasin declare les voit tous — c'est le sens de
        // « Tous les magasins » dans son profil.
        if (
          selectedStore === "cat:FOURNISSEUR" &&
          user?.role !== UserRole.ADMIN &&
          (user as any)?.storeId
        ) {
          conversationsData = conversationsData.filter(
            (c: any) => c.storeId === (user as any).storeId,
          );
        }

        // Calculer le total des unread pour détecter les nouveaux messages
        const totalUnread = conversationsData.reduce(
          (sum, c) => sum + (c.unreadCount || 0),
          0,
        );

        // Jouer le son si un nouveau message est arrivé (unreadCount a augmenté)
        if (
          totalUnread > previousUnreadCountRef.current &&
          previousUnreadCountRef.current > 0
        ) {
          // Son de notification seulement (pas de notification browser)
          // Firebase Messaging gère l'affichage des notifications
          playNotificationSound();
        }

        previousUnreadCountRef.current = totalUnread;
        setConversations(conversationsData);
        setLoadingConversations(false);

        // 🔥 Auto-sélectionner la conversation si URL param présent
        if (urlParams.conversation && conversationsData.length > 0) {
          const conv = conversationsData.find((c) => c.id === urlParams.conversation);
          if (conv && !selectedConversation) {
            handleConversationClick(conv);
          }
        }
      },
      (error) => {
        console.error("Error loading conversations:", error);
        setLoadingConversations(false);
      },
    );

    return () => unsubscribe();
  }, [selectedStore, user?.role]);

  // Load messages from Twilio API when conversation changes + polling
  useEffect(() => {
    if (!selectedConversation) return;

    // Utiliser le storeId de la conversation si "TOUS LES STORES" est sélectionné
    const storeIdToUse = selectedStore || selectedConversation.storeId;
    if (!storeIdToUse) return;

    // Reset pour nouvelle conversation
    isInitialLoadRef.current = true;
    previousMessageIdsRef.current = new Set();
    lastMessageAtRef.current = null;

    const loadMessagesFromTwilio = async () => {
      try {
        if (isInitialLoadRef.current) {
          setLoadingMessages(true);
        }

        // Charger les 20 derniers messages depuis Twilio
        const messagesData = await api.messaging.getMessages(
          storeIdToUse,
          selectedConversation.contactNumber,
          // Cent messages : de quoi remonter plusieurs jours d'echange.
          // Vingt coupait les conversations en plein milieu, et on croyait
          // le debut perdu alors qu'il n'avait jamais ete demande.
          100,
        );

        // Comparer les IDs des messages
        const currentIds = new Set(messagesData.map((m: any) => m.id));
        const previousIds = previousMessageIdsRef.current;

        // Vérifier si changement
        const hasChanged =
          currentIds.size !== previousIds.size ||
          [...currentIds].some((id) => !previousIds.has(id));

        console.log(
          `🔄 Loading: ${messagesData.length} messages, changed: ${hasChanged}`,
        );

        // Mettre à jour SEULEMENT si changement
        if (hasChanged || isInitialLoadRef.current) {
          const hasNewMessages =
            currentIds.size > previousIds.size && !isInitialLoadRef.current;
          previousMessageIdsRef.current = currentIds;

          console.log(`✅ Updating messages, new: ${hasNewMessages}`);
          setMessages(messagesData);

          // Scroll to bottom SEULEMENT si nouveaux messages ou premier chargement
          if (isInitialLoadRef.current || hasNewMessages) {
            setTimeout(() => {
              messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            }, 100);
          }

          isInitialLoadRef.current = false;
        }
      } catch (error) {
        console.error("Error loading messages from Twilio:", error);
      } finally {
        setLoadingMessages(false);
      }
    };

    // Charge initial
    loadMessagesFromTwilio();

    // Polling toutes les 10 secondes pour détecter les nouveaux messages —
    // SEULEMENT onglet visible (06/10/2026). Onglet cache : les nouvelles
    // conversations restent signalees par l'ecoute du menu ; au retour, la
    // conversation ouverte est relue aussitot.
    return intervalleVisible(() => {
      loadMessagesFromTwilio();
    }, 10000);
  }, [selectedConversation?.id, selectedStore]);

  // Handle store change
  const handleStoreChange = (storeId: string) => {
    setSelectedStore(storeId);
    setSelectedConversation(null);
    setMessages([]);
    setShowMessages(false);
  };

  // 🆕 Démarrer une nouvelle conversation avec un numéro saisi manuellement
  // Utilise un message-modèle WhatsApp pré-approuvé (obligatoire pour un premier contact)
  const handleStartNewConversation = async () => {
    if (!selectedStore) {
      alert("Veuillez d'abord choisir une boutique (pas 'TOUS LES STORES').");
      return;
    }
    if (!newConvPhone.trim()) {
      alert("Veuillez entrer un numéro de téléphone.");
      return;
    }

    setSendingNewConv(true);
    try {
      // Normaliser le numéro : accepte 06.., 07.., +2126.., 2126..
      let phone = newConvPhone.trim().replace(/[\s\-\.]/g, "");
      if (phone.startsWith("00")) phone = `+${phone.slice(2)}`;
      if (!phone.startsWith("+")) {
        if (phone.startsWith("0")) phone = `+212${phone.slice(1)}`;
        else if (phone.startsWith("212")) phone = `+${phone}`;
        else phone = `+212${phone}`;
      }

      const storeName = stores.find((s) => s.id === selectedStore)?.name || "Natura Beldi";

      // Récupérer le template WhatsApp configuré pour cette boutique
      let templateSid = "HX22e0cbee729d0f6a6d038640573b4d2d"; // valeur par défaut
      try {
        const settings = await api.stores.getMessagingSettings(selectedStore);
        if (settings?.messagingTemplateSid) templateSid = settings.messagingTemplateSid;
      } catch (e) {
        console.warn("Impossible de charger le template du store, utilisation du template par défaut", e);
      }

      await api.messaging.sendTemplateMessage(selectedStore, phone, templateSid, {
        "1": newConvName.trim() || "Client",
        "2": newConvOrderCode.trim() || "-",
        "3": storeName,
        "4": "-",
        "5": "-",
      });

      alert(`✅ Message envoyé à ${phone}. Le client peut maintenant répondre sur WhatsApp.`);
      setShowNewConvModal(false);
      setNewConvPhone("");
      setNewConvName("");
      setNewConvOrderCode("");
    } catch (error: any) {
      console.error("Error starting new conversation:", error);
      alert(`❌ Erreur: ${error.message}`);
    } finally {
      setSendingNewConv(false);
    }
  };

  // Handle conversation click
  const handleConversationClick = async (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setShowMessages(true);

    // Mark as read
    if (conversation.unreadCount > 0) {
      try {
        // 🔥 Utiliser le storeId de la conversation
        await api.messaging.markAsRead(conversation.storeId, conversation.id);
      } catch (error) {
        console.error("Error marking as read:", error);
      }
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setSendingMessage(true);
    const messageBody = newMessage.trim();

    try {
      // 🔥 Utiliser le storeId de la conversation (pas selectedStore)
      const storeIdToUse = selectedConversation.storeId;

      // 🔥 Ajouter le message localement IMMÉDIATEMENT (optimistic update)
      const optimisticMessage = {
        id: `temp-${Date.now()}`,
        conversationId: selectedConversation.id,
        twilioSid: "",
        direction: "outbound" as const,
        fromNumber: "",
        toNumber: selectedConversation.contactNumber,
        body: messageBody,
        status: "sending",
        sentAt: new Date(),
        hasMedia: false,
        isRead: true,
        isAutomatic: false,
      };

      setMessages((prev) => [...prev, optimisticMessage]);
      setNewMessage("");

      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 50);

      // Envoyer le message
      await api.messaging.sendMessage(
        storeIdToUse,
        selectedConversation.contactNumber,
        messageBody,
      );
    } catch (error: any) {
      console.error("Error sending message:", error);
      alert(`Erreur: ${error.message}`);
      // Retirer le message optimiste en cas d'erreur
      setMessages((prev) => prev.filter((m) => !m.id.startsWith("temp-")));
    } finally {
      setSendingMessage(false);
    }
  };

  // Sync messages from Twilio
  // A l'ouverture de la Messagerie, on remet les categories a jour.
  // Silencieux par choix : c'est une remise en ordre, pas une action de
  // l'utilisateur — et si elle echoue, la page reste parfaitement lisible.
  useEffect(() => {
    api.messaging.reclasser().catch(() => {});
  }, []);

  const handleSyncMessages = async () => {
    if (!selectedStore) return;

    setSyncingMessages(true);
    try {
      const result = await api.messaging.syncMessages(selectedStore, 50);
      alert(
        `✅ Synchronisation terminée:\n- ${result.stats.synced} messages ajoutés\n- ${result.stats.skipped} ignorés\n- ${result.stats.errors} erreurs`,
      );
    } catch (error: any) {
      console.error("Error syncing messages:", error);
      alert(`❌ Erreur lors de la synchronisation: ${error.message}`);
    } finally {
      setSyncingMessages(false);
    }
  };

  // Handle Enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Toggle tag on conversation
  const handleToggleTag = async (tagId: string) => {
    if (!selectedConversation || updatingTags) return;

    setUpdatingTags(true);
    try {
      const currentTags = selectedConversation.tags || [];
      const newTags = currentTags.includes(tagId)
        ? currentTags.filter((t) => t !== tagId)
        : [...currentTags, tagId];

      await api.messaging.updateConversationTags(
        selectedConversation.id,
        newTags,
      );

      // Mettre à jour localement
      setSelectedConversation({ ...selectedConversation, tags: newTags });
      setConversations((prev) =>
        prev.map((c) =>
          c.id === selectedConversation.id ? { ...c, tags: newTags } : c,
        ),
      );
    } catch (error: any) {
      console.error("Error updating tags:", error);
      alert(`Erreur: ${error.message}`);
    } finally {
      setUpdatingTags(false);
    }
  };

  // Toggle pin on conversation
  const handleTogglePin = async () => {
    if (!selectedConversation || updatingTags) return;

    setUpdatingTags(true);
    try {
      const newPinned = !selectedConversation.isPinned;

      // Mettre à jour dans Firestore
      const conversationRef = collection(db, COLLECTIONS.CONVERSATIONS);
      const { doc, updateDoc } = await import("firebase/firestore");
      await updateDoc(doc(conversationRef, selectedConversation.id), {
        isPinned: newPinned,
        updatedAt: new Date(),
      });

      // Mettre à jour localement
      setSelectedConversation({ ...selectedConversation, isPinned: newPinned });
      setConversations((prev) =>
        prev.map((c) =>
          c.id === selectedConversation.id ? { ...c, isPinned: newPinned } : c,
        ),
      );
    } catch (error: any) {
      console.error("Error toggling pin:", error);
      alert(`Erreur: ${error.message}`);
    } finally {
      setUpdatingTags(false);
    }
  };

  // Format phone number
  const formatPhone = (phone: string) => {
    return phone.replace(/(\+\d{3})(\d{3})(\d{3})(\d{3})/, "$1 $2 $3 $4");
  };

  // 🔥 Générer une couleur unique par store (mapping fixe par index)
  const getStoreColor = (storeId: string) => {
    const colors = [
      { bg: "bg-blue-500", text: "text-white", border: "border-blue-600" }, // Bleu foncé
      { bg: "bg-green-500", text: "text-white", border: "border-green-600" }, // Vert foncé
      { bg: "bg-purple-500", text: "text-white", border: "border-purple-600" }, // Violet
      { bg: "bg-orange-500", text: "text-white", border: "border-orange-600" }, // Orange
      { bg: "bg-pink-500", text: "text-white", border: "border-pink-600" }, // Rose
      { bg: "bg-indigo-600", text: "text-white", border: "border-indigo-700" }, // Indigo foncé
      { bg: "bg-teal-500", text: "text-white", border: "border-teal-600" }, // Teal
      { bg: "bg-red-500", text: "text-white", border: "border-red-600" }, // Rouge
      {
        bg: "bg-yellow-500",
        text: "text-gray-900",
        border: "border-yellow-600",
      }, // Jaune (texte noir)
      {
        bg: "bg-emerald-500",
        text: "text-white",
        border: "border-emerald-600",
      }, // Émeraude
    ];

    // Trouver l'index du store dans la liste triée (garantit cohérence)
    const storeIndex = stores.findIndex((s) => s.id === storeId);
    return colors[storeIndex >= 0 ? storeIndex % colors.length : 0];
  };

  // Format date - corrigé pour gérer correctement les timestamps Firestore
  const formatDate = (date: any) => {
    if (!date) return "";

    let d: Date;
    try {
      if (date.toDate && typeof date.toDate === "function") {
        // Timestamp Firestore
        d = date.toDate();
      } else if (date instanceof Date) {
        d = date;
      } else if (typeof date === "number") {
        d = new Date(date);
      } else if (date.seconds && typeof date.seconds === "number") {
        // Timestamp Firestore avec seconds
        d = new Date(date.seconds * 1000 + (date.nanoseconds || 0) / 1000000);
      } else if (date._seconds) {
        // Format alternatif Firestore
        d = new Date(date._seconds * 1000);
      } else {
        d = new Date(date);
      }

      // Vérifier que la date est valide
      if (isNaN(d.getTime())) {
        console.warn("Invalid date:", date);
        return "";
      }

      return formatDistanceToNow(d, { addSuffix: true, locale: fr });
    } catch (error) {
      console.error("Error formatting date:", date, error);
      return "";
    }
  };

  // Loading state
  if (loadingStores) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div
      className="h-[calc(100vh-100px)] md:h-[calc(100vh-120px)] flex flex-col overflow-hidden w-full min-w-0"
      style={{ maxWidth: "100%" }}
    >
      {/* Header - Mobile optimized */}
      <div className="flex-shrink-0 mb-2 md:mb-4 px-2 md:px-0 max-w-full">
        <div className="flex flex-col gap-2 md:gap-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-lg md:text-2xl font-bold text-gray-900 truncate">
                Messagerie WhatsApp
              </h1>
              <p className="text-gray-500 text-xs md:text-sm hidden md:block">
                Gérez vos conversations par store
              </p>
            </div>
          </div>

          {/* Store selector and sync button - Mobile optimized */}
          <div className="flex gap-2">
            <select
              value={selectedStore}
              onChange={(e) => handleStoreChange(e.target.value)}
              className="flex-1 px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">🏪 TOUS LES STORES</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}{" "}
                  {store.twilioNumber ? `(${store.twilioNumber})` : ""}
                </option>
              ))}

              {/* Les profils, par categorie.
                  Collaborateurs et equipe ne sont pas seulement masques a
                  l'affichage : l'option n'existe pas pour un compte support.
                  On ne cache pas une porte, on ne la construit pas. */}
              <option disabled>──────────</option>
              <option value="cat:FOURNISSEUR">📦 FOURNISSEURS</option>
              {user?.role === UserRole.ADMIN && (
                <>
                  <option value="cat:COLLABORATEUR">👥 COLLABORATEURS</option>
                  <option value="cat:EQUIPE">🎧 SUPPORT &amp; ÉQUIPE</option>
                </>
              )}
              {/* Les numeros que la base ne reconnait pas. Souvent de
                  nouveaux clients : une entree a part vaut mieux qu'un
                  message noye dans la liste generale. */}
              <option value="cat:INCONNU">❓ NON IDENTIFIÉS</option>
            </select>
            <button
              onClick={handleSyncMessages}
              disabled={!selectedStore || syncingMessages || !hasCredential}
              className="px-3 md:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 md:gap-2 min-w-[44px]"
              title="Synchroniser les messages depuis Twilio"
            >
              <RefreshCw
                className={`w-4 h-4 ${syncingMessages ? "animate-spin" : ""}`}
              />
              <span className="hidden md:inline">
                {syncingMessages ? "Sync..." : "Synchroniser"}
              </span>
            </button>
            {/* 🆕 Nouvelle conversation - saisie manuelle d'un numéro par le responsable */}
            <button
              onClick={() => setShowNewConvModal(true)}
              disabled={!selectedStore}
              className="px-3 md:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 md:gap-2 min-w-[44px]"
              title="Démarrer une nouvelle conversation avec un numéro"
            >
              <Send className="w-4 h-4" />
              <span className="hidden md:inline">Nouveau</span>
            </button>
          </div>
        </div>

        {/* Credential warning - Mobile optimized */}
        {!hasCredential && selectedStore && (
          <div className="mt-2 md:mt-4 p-3 md:p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2 md:gap-3">
            <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-yellow-800 font-medium text-sm md:text-base">
                Credential Twilio non configurée
              </p>
              <p className="text-yellow-700 text-xs md:text-sm mt-1">
                Ce store n'a pas de numéro WhatsApp configuré.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Main content - Mobile optimized */}
      <div className="flex-1 flex overflow-hidden bg-white rounded-lg md:rounded-xl border border-gray-200 shadow-sm w-full min-w-0">
        {/* Conversations list */}
        <div
          className={`${
            showMessages ? "hidden" : "flex"
          } md:flex md:w-1/3 lg:w-1/4 w-full border-r border-gray-200 flex-col`}
        >
          {/* Conversations header - Mobile optimized */}
          <div className="p-3 md:p-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
              <span className="font-semibold text-sm md:text-base">
                Conversations
              </span>
              {/* Le compteur suit le filtre. Afficher 99 au-dessus d'une
                  liste de trois lignes ferait croire a un chargement
                  incomplet. */}
              {conversations.length > 0 && (
                <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">
                  {showUnreadOnly
                    ? conversations.filter((c) => c.unreadCount > 0).length
                    : showInconnusOnly
                      ? conversations.filter(
                          (c) => (c as any).categorie === "INCONNU",
                        ).length
                      : conversations.length}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
            {/* LES NON IDENTIFIES, EN UN CLIC.
                Ces conversations sont celles qui posent probleme : pas de
                magasin, envoi bloque, Amina en difficulte. Le compteur dit
                combien il en reste a rattacher. */}
            {conversations.some((c) => (c as any).categorie === "INCONNU") && (
              <button
                onClick={() => {
                  setShowInconnusOnly(!showInconnusOnly);
                  setShowUnreadOnly(false);
                }}
                className={`px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  showInconnusOnly
                    ? "bg-gray-700 text-white hover:bg-gray-800"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                title="Numéros absents de l'annuaire : leur conversation n'est rattachée à aucun magasin, l'envoi est bloqué et Amina ne peut pas répondre. Crée leur fiche dans Utilisateurs, Collaborateurs ou Fournisseurs."
              >
                <span>❓</span>
                <span className="hidden md:inline">
                  Non identifiés{" "}
                  {conversations.filter(
                    (c) => (c as any).categorie === "INCONNU",
                  ).length}
                </span>
              </button>
            )}
            {/* 🔥 Bouton filtre non lus */}
            <button
              onClick={() => {
                setShowUnreadOnly(!showUnreadOnly);
                setShowInconnusOnly(false);
              }}
              className={`px-2 md:px-3 py-1 md:py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors flex items-center gap-1.5 ${
                showUnreadOnly
                  ? "bg-red-100 text-red-700 hover:bg-red-200"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              title={
                showUnreadOnly
                  ? "Afficher toutes les conversations"
                  : "Afficher uniquement les non lus"
              }
            >
              <span className="relative">
                <MessageSquare className="w-3.5 h-3.5 md:w-4 md:h-4" />
                {showUnreadOnly && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </span>
              <span className="hidden md:inline">
                {showUnreadOnly ? "Non lus" : "Tous"}
              </span>
            </button>
            </div>
          </div>

          {/* Conversations list */}
          <div className="flex-1 overflow-y-auto">
            {loadingConversations ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Aucune conversation</p>
                <p className="text-sm">Les messages apparaîtront ici</p>
              </div>
            ) : (
              // 📌 Trier: Pinned en haut, puis par date
              [...conversations]
                .filter((conv) => {
                  // 🔥 Filtre: si showUnreadOnly est activé, afficher seulement les non lus
                  if (showUnreadOnly) {
                    return conv.unreadCount > 0;
                  }
                  // LES NUMEROS QU'ON NE SAIT PAS NOMMER.
                  //
                  // Un numero absent de l'annuaire arrive en « INCONNU » :
                  // sa conversation n'est rattachee a aucun magasin, l'envoi
                  // manuel est bloque et Amina peine a repondre. Les voir
                  // ensemble permet de creer les fiches manquantes d'un coup
                  // plutot que de les decouvrir une par une.
                  if (showInconnusOnly) {
                    return (conv as any).categorie === "INCONNU";
                  }
                  return true;
                })
                .sort((a, b) => {
                  // Les pinned en premier
                  if (a.isPinned && !b.isPinned) return -1;
                  if (!a.isPinned && b.isPinned) return 1;

                  // Sinon, trier par date (plus récent en premier)
                  const dateA = a.lastMessageAt?.toDate?.() || new Date(0);
                  const dateB = b.lastMessageAt?.toDate?.() || new Date(0);
                  return dateB.getTime() - dateA.getTime();
                })
                .map((conv) => {
                  // 🏷️ Obtenir le tag principal (premier tag) pour colorer toute la conversation
                  const primaryTag =
                    conv.tags && conv.tags.length > 0
                      ? AVAILABLE_TAGS.find((t) => t.id === conv.tags![0])
                      : null;

                  return (
                    <div
                      key={conv.id}
                      onClick={() => handleConversationClick(conv)}
                      className={`p-3 md:p-4 border-b border-gray-100 cursor-pointer transition-colors ${
                        selectedConversation?.id === conv.id
                          ? "border-l-4 border-l-green-500"
                          : primaryTag
                            ? "border-l-4"
                            : ""
                      }`}
                      style={{
                        backgroundColor: primaryTag
                          ? `${primaryTag.color}15` // 15 = 8% opacity en hex
                          : selectedConversation?.id === conv.id
                            ? "#f0fdf4"
                            : "transparent",
                        borderLeftColor: primaryTag
                          ? primaryTag.color
                          : undefined,
                      }}
                    >
                      <div className="flex items-start gap-2 md:gap-3">
                        {/* Avatar */}
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-green-700 font-medium text-xs md:text-sm">
                            {conv.contactNumber.slice(-2)}
                          </span>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              {/* 📌 Indicateur pinned */}
                              {conv.isPinned && (
                                <Pin className="w-3 h-3 text-yellow-600 fill-current flex-shrink-0" />
                              )}
                              <p className="font-medium text-gray-900 truncate text-sm md:text-base">
                                {conv.contactName ||
                                  formatPhone(conv.contactNumber)}
                              </p>
                              {/* La pastille dit A QUI on parle.
                                  Pour un fournisseur, un collaborateur ou
                                  quelqu'un de l'equipe, le role est
                                  l'information utile — pas le magasin de sa
                                  derniere commande, qui le ferait passer pour
                                  un client. Le magasin reste affiche pour les
                                  clients, ou la il compte vraiment. */}
                              {(() => {
                                const roles: Record<
                                  string,
                                  { texte: string; bg: string; text: string }
                                > = {
                                  FOURNISSEUR: {
                                    texte: "📦 Fournisseur",
                                    bg: "bg-amber-100",
                                    text: "text-amber-800",
                                  },
                                  COLLABORATEUR: {
                                    texte: "👥 Collaborateur",
                                    bg: "bg-indigo-100",
                                    text: "text-indigo-800",
                                  },
                                  EQUIPE: {
                                    texte: "🎧 Support",
                                    bg: "bg-purple-100",
                                    text: "text-purple-800",
                                  },
                                  INCONNU: {
                                    texte: "❓ Non identifié",
                                    bg: "bg-gray-200",
                                    text: "text-gray-700",
                                  },
                                };
                                const role = roles[(conv as any).categorie];
                                if (role) {
                                  return (
                                    <span
                                      className={`${role.bg} ${role.text} text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-medium`}
                                    >
                                      {role.texte}
                                    </span>
                                  );
                                }
                                const color = getStoreColor(conv.storeId);
                                return (
                                  <span
                                    className={`${color.bg} ${color.text} text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-medium`}
                                  >
                                    {stores.find((s) => s.id === conv.storeId)
                                      ?.name || "Store"}
                                  </span>
                                );
                              })()}
                            </div>
                            {conv.unreadCount > 0 && (
                              <span className="bg-red-500 text-white text-xs px-1.5 md:px-2 py-0.5 rounded-full flex-shrink-0">
                                {conv.unreadCount}
                              </span>
                            )}
                          </div>
                          <p className="text-xs md:text-sm text-gray-500 truncate mt-0.5">
                            {conv.lastMessage || "Pas de message"}
                          </p>
                          <div className="flex items-center justify-between gap-2 mt-1">
                            {conv.lastMessageAt && (
                              <p className="text-xs text-gray-400">
                                {formatDate(conv.lastMessageAt)}
                              </p>
                            )}
                            {/* 🏷️ Afficher les tags sous forme de badges */}
                            {(conv.tags?.length || 0) > 0 && (
                              <div className="flex gap-1 flex-wrap">
                                {conv.tags?.map((tagId) => {
                                  const tag = AVAILABLE_TAGS.find(
                                    (t) => t.id === tagId,
                                  );
                                  if (!tag) return null;
                                  return (
                                    <span
                                      key={tagId}
                                      className="text-xs px-1.5 py-0.5 rounded text-white font-medium"
                                      style={{ backgroundColor: tag.color }}
                                    >
                                      {tag.label.split(" ")[0]}{" "}
                                      {/* Juste l'emoji */}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        </div>

        {/* Messages panel */}
        <div
          className={`${
            showMessages ? "flex" : "hidden"
          } md:flex flex-1 flex-col min-w-0 overflow-hidden w-full`}
        >
          {selectedConversation ? (
            <>
              {/* Message header - Mobile optimized */}
              <div className="p-2 md:p-4 border-b border-gray-200 flex items-center gap-1.5 md:gap-3 bg-white flex-shrink-0">
                {/* Back button (mobile) */}
                <button
                  onClick={() => setShowMessages(false)}
                  className="md:hidden p-1.5 hover:bg-gray-100 active:bg-gray-200 rounded-lg"
                  aria-label="Retour"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <a
                  href={`tel:${selectedConversation.contactNumber}`}
                  className="w-9 h-9 md:w-12 md:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 hover:bg-green-200 active:bg-green-300 transition-colors"
                  title={`Appeler ${selectedConversation.contactNumber}`}
                >
                  <Phone className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                </a>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="font-medium text-sm md:text-base truncate">
                      {selectedConversation.contactName ||
                        formatPhone(selectedConversation.contactNumber)}
                    </p>
                    {/* 🔥 Badge du store - hidden sur mobile */}
                    {(() => {
                      const color = getStoreColor(selectedConversation.storeId);
                      return (
                        <span
                          className={`hidden md:inline-flex ${color.bg} ${color.text} text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-medium`}
                        >
                          {stores.find(
                            (s) => s.id === selectedConversation.storeId,
                          )?.name || "Store"}
                        </span>
                      );
                    })()}
                  </div>
                  {/* Numéro caché sur mobile */}
                  <p className="hidden md:block text-xs md:text-sm text-gray-500 truncate">
                    {selectedConversation.contactNumber}
                  </p>
                </div>
                {/* 📌 Bouton Pin - compact sur mobile, normal sur desktop */}
                <button
                  onClick={handleTogglePin}
                  disabled={updatingTags}
                  className={`p-1.5 md:p-2 rounded-lg flex items-center gap-1 flex-shrink-0 transition-colors ${
                    selectedConversation.isPinned
                      ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  } ${updatingTags ? "opacity-50" : ""}`}
                  title={
                    selectedConversation.isPinned ? "Dépingler" : "Épingler"
                  }
                >
                  <Pin
                    className={`w-4 h-4 ${selectedConversation.isPinned ? "fill-current" : ""}`}
                  />
                </button>

                {/* 🔥 Bouton commandes - compact sur mobile */}
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      // Chercher le client dans la DB par son numéro
                      const phone = selectedConversation.contactNumber.replace(
                        /\s/g,
                        "",
                      );

                      // Chercher dans la collection customers
                      const { collection, query, getDocs, limit } =
                        await import("firebase/firestore");
                      const customersRef = collection(
                        db,
                        COLLECTIONS.CUSTOMERS,
                      );

                      // Normaliser pour la recherche (enlever +)
                      const phoneDigits = phone.replace(/[\+\s\-]/g, "");

                      const q = query(customersRef, limit(100));

                      const snapshot = await getDocs(q);
                      let foundPhone = phone;

                      // Chercher le client qui correspond
                      for (const doc of snapshot.docs) {
                        const customerData = doc.data();
                        const customerPhoneDigits = (
                          customerData.phone || ""
                        ).replace(/[\+\s\-]/g, "");

                        if (
                          customerPhoneDigits.includes(phoneDigits) ||
                          phoneDigits.includes(customerPhoneDigits)
                        ) {
                          foundPhone = customerData.phone;
                          console.log(
                            "Found customer in DB:",
                            doc.id,
                            "phone:",
                            foundPhone,
                          );
                          break;
                        }
                      }

                      const basePath =
                        user?.role === UserRole.SUPPORT ? "/support" : "/admin";
                      const url = `${basePath}/orders?phone=${encodeURIComponent(foundPhone)}`;
                      console.log(
                        "Opening orders page with phone:",
                        foundPhone,
                        "URL:",
                        url,
                      );
                      window.open(url, "_blank");
                    } catch (error) {
                      console.error("Error finding customer:", error);
                      // Fallback: utiliser le numéro de la conversation
                      const phone = selectedConversation.contactNumber;
                      const basePath =
                        user?.role === UserRole.SUPPORT ? "/support" : "/admin";
                      window.open(
                        `${basePath}/orders?phone=${encodeURIComponent(phone)}`,
                        "_blank",
                      );
                    }
                  }}
                  className="p-2 md:px-3 md:py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 active:bg-blue-700 text-xs md:text-sm font-medium flex items-center justify-center gap-1 flex-shrink-0"
                  title="Voir les commandes"
                >
                  <span className="text-base md:text-sm">📦</span>
                  <span className="hidden md:inline">Commandes</span>
                </button>

                {/* 🏷️ Bouton tags - compact sur mobile */}
                <div className="relative">
                  <button
                    onClick={() => setShowTagMenu(!showTagMenu)}
                    className={`p-1.5 md:p-2 rounded-lg flex items-center gap-1 flex-shrink-0 ${
                      (selectedConversation.tags?.length || 0) > 0
                        ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    title="Gérer les tags"
                  >
                    <Tag className="w-4 h-4" />
                    {(selectedConversation.tags?.length || 0) > 0 && (
                      <span className="text-xs font-bold">
                        {selectedConversation.tags?.length}
                      </span>
                    )}
                  </button>

                  {/* Menu des tags */}
                  {showTagMenu && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
                      <div className="px-3 py-2 border-b border-gray-100">
                        <p className="text-xs font-semibold text-gray-500 uppercase">
                          Tags
                        </p>
                      </div>
                      {AVAILABLE_TAGS.map((tag) => {
                        const isSelected = selectedConversation.tags?.includes(
                          tag.id,
                        );
                        return (
                          <button
                            key={tag.id}
                            onClick={() => handleToggleTag(tag.id)}
                            disabled={updatingTags}
                            className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 ${
                              updatingTags ? "opacity-50" : ""
                            }`}
                          >
                            <span
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: tag.color }}
                            />
                            <span className="flex-1">{tag.label}</span>
                            {isSelected && (
                              <Check className="w-4 h-4 text-green-600" />
                            )}
                          </button>
                        );
                      })}
                      <div className="border-t border-gray-100 mt-1 pt-1">
                        <button
                          onClick={() => setShowTagMenu(false)}
                          className="w-full px-3 py-2 text-left text-xs text-gray-500 hover:bg-gray-50"
                        >
                          Fermer
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 🏷️ Affichage des tags actifs */}
              {(selectedConversation.tags?.length || 0) > 0 && (
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex flex-wrap gap-1">
                  {selectedConversation.tags?.map((tagId) => {
                    const tag = AVAILABLE_TAGS.find((t) => t.id === tagId);
                    if (!tag) return null;
                    return (
                      <span
                        key={tagId}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                        style={{ backgroundColor: tag.color }}
                      >
                        {tag.label}
                        <button
                          onClick={() => handleToggleTag(tagId)}
                          className="hover:opacity-70"
                          disabled={updatingTags}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Messages - FIXED HEIGHT with scroll */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 md:p-4 space-y-2 md:space-y-3 bg-gray-50 min-w-0">
                {loadingMessages ? (
                  <div className="flex items-center justify-center h-32">
                    <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <p className="text-sm md:text-base">Aucun message</p>
                  </div>
                ) : (
                  <>
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex w-full ${msg.direction === "outbound" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[85%] md:max-w-[70%] px-3 md:px-4 py-2 rounded-2xl overflow-hidden break-words ${
                            msg.isAutomatic
                              ? "bg-blue-50 text-gray-900 border-2 border-blue-200 rounded-br-sm"
                              : msg.direction === "outbound"
                                ? "bg-green-500 text-white rounded-br-sm"
                                : "bg-white text-gray-900 rounded-bl-sm shadow-sm"
                          }`}
                        >
                          {/* Qui a ecrit. Un message sortant sans nom serait
                              un message qu'on ne peut pas attribuer : on
                              affiche toujours quelque chose. */}
                          {msg.direction === "outbound" && msg.auteurNom && (
                            <p
                              className={`text-[11px] font-semibold mb-1 ${
                                msg.isAutomatic
                                  ? "text-blue-600"
                                  : "text-white/80"
                              }`}
                            >
                              {msg.auteurNom}
                              {msg.auteurSur === false && " ·"}
                            </p>
                          )}

                          {/* Badge message automatique */}
                          {msg.isAutomatic && (
                            <div className="flex items-center gap-1 mb-2 pb-2 border-b border-blue-200">
                              <span className="text-xs font-bold text-blue-600">
                                🤖 MESSAGE AUTOMATIQUE
                              </span>
                              {msg.isTestMode && (
                                <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                                  TEST
                                </span>
                              )}
                              {msg.orderCode && (
                                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                                  #{msg.orderCode}
                                </span>
                              )}
                            </div>
                          )}

                          <p
                            className="text-sm md:text-base whitespace-pre-wrap"
                            style={{
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {msg.body}
                          </p>

                          {/* Media */}
                          {msg.hasMedia &&
                            msg.mediaUrl &&
                            (() => {
                              const mediaProxyUrl = getMediaUrl(
                                msg.mediaUrl,
                                selectedConversation?.storeId || selectedStore,
                              );
                              if (!mediaProxyUrl) return null;

                              const isImage =
                                msg.mediaType?.startsWith("image/");
                              const isVideo =
                                msg.mediaType?.startsWith("video/");
                              const isAudio =
                                msg.mediaType?.startsWith("audio/") ||
                                msg.mediaType === "audio/ogg";

                              return (
                                <div className="mt-2">
                                  {isImage && (
                                    <img
                                      src={mediaProxyUrl}
                                      alt="Media"
                                      className="max-w-[250px] rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                      onClick={() =>
                                        window.open(mediaProxyUrl, "_blank")
                                      }
                                      onError={(e) => {
                                        (
                                          e.target as HTMLImageElement
                                        ).style.display = "none";
                                        (
                                          e.target as HTMLImageElement
                                        ).nextElementSibling?.classList.remove(
                                          "hidden",
                                        );
                                      }}
                                    />
                                  )}
                                  {isVideo && (
                                    <video
                                      src={mediaProxyUrl}
                                      controls
                                      className="max-w-[250px] rounded-lg"
                                    >
                                      Vidéo non supportée
                                    </video>
                                  )}
                                  {isAudio && (
                                    <div className="bg-gray-100 rounded-lg p-2 max-w-[250px]">
                                      <audio
                                        src={mediaProxyUrl}
                                        controls
                                        className="w-full h-10"
                                      >
                                        Audio non supporté
                                      </audio>
                                    </div>
                                  )}
                                  {!isImage && !isVideo && !isAudio && (
                                    <a
                                      href={mediaProxyUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
                                    >
                                      📎{" "}
                                      {msg.mediaType
                                        ?.split("/")[1]
                                        ?.toUpperCase() || "Fichier"}
                                    </a>
                                  )}
                                  {/* Fallback si l'image ne charge pas */}
                                  <a
                                    href={mediaProxyUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hidden text-sm underline text-blue-500"
                                  >
                                    📎 Ouvrir le média
                                  </a>
                                </div>
                              );
                            })()}

                          {/* Time and status */}
                          <div className="flex items-center justify-end gap-1 mt-1">
                            <span
                              className={`text-xs ${
                                msg.isAutomatic
                                  ? "text-blue-600"
                                  : msg.direction === "outbound"
                                    ? "text-green-100"
                                    : "text-gray-400"
                              }`}
                            >
                              {formatDate(msg.sentAt)}
                            </span>
                            {msg.direction === "outbound" &&
                              !msg.isAutomatic && (
                                <span className="text-xs text-green-100">
                                  {msg.status === "delivered"
                                    ? "✓✓"
                                    : msg.status === "read"
                                      ? "✓✓"
                                      : "✓"}
                                </span>
                              )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Input - FIXED at bottom, textarea NEVER grows */}
              <div className="p-3 md:p-4 border-t border-gray-200 bg-white flex-shrink-0">
                <div className="flex gap-2 items-start">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Tapez votre message..."
                    className="flex-1 px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-green-500 focus:border-green-500 overflow-y-auto"
                    rows={2}
                    disabled={sendingMessage || !hasCredential}
                    style={{ height: "60px", maxHeight: "60px" }}
                  />
                  {/* Réponses rapides (remplit le texte sans envoyer) */}
                  <div className="relative flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowQuickReplies(!showQuickReplies)}
                      className="h-[60px] px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-600 flex items-center justify-center gap-1"
                      title="Réponses rapides"
                      aria-label="Réponses rapides"
                    >
                      <FileText className="w-5 h-5" />
                      <span className="hidden md:inline text-sm">Réponses</span>
                    </button>
                    {showQuickReplies && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          aria-hidden="true"
                          onClick={() => setShowQuickReplies(false)}
                        />
                        <div className="absolute bottom-full right-0 mb-2 w-80 max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                          <div className="px-3 py-2 border-b border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase">
                              Réponses rapides (remplir le message)
                            </p>
                          </div>
                          {QUICK_REPLIES.map((item) => (
                            <button
                              key={item.label}
                              type="button"
                              onClick={() => {
                                setNewMessage(item.text);
                                setShowQuickReplies(false);
                              }}
                              className="w-full px-3 py-2.5 text-left text-sm hover:bg-green-50 border-b border-gray-50 last:border-0"
                            >
                              <span className="font-medium text-gray-700 block truncate">
                                {item.label}
                              </span>
                              <span className="text-xs text-gray-500 line-clamp-2 mt-0.5">
                                {item.text}
                              </span>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={handleSendMessage}
                    disabled={
                      !newMessage.trim() || sendingMessage || !hasCredential
                    }
                    className="px-3 md:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 active:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[44px]"
                    style={{ height: "60px" }}
                    aria-label="Envoyer"
                  >
                    {sendingMessage ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {!hasCredential && (
                  <p className="text-xs md:text-sm text-red-500 mt-2">
                    Configurez une credential Twilio pour envoyer des messages
                  </p>
                )}
              </div>
            </>
          ) : (
            /* No conversation selected */
            <div className="flex-1 flex items-center justify-center text-gray-500 p-4">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 md:mb-4 text-gray-300" />
                <p className="text-base md:text-lg font-medium">
                  Sélectionnez une conversation
                </p>
                <p className="text-sm md:text-base mt-1">
                  pour voir les messages
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 🆕 Modal Nouvelle conversation */}
      {showNewConvModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">
                Nouvelle conversation
              </h2>
              <button
                onClick={() => setShowNewConvModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Entrez le numéro du client (récupéré via l'app Glovo,
              autorisation AM). Un premier message automatique lui sera
              envoyé pour l'inviter à discuter avec vous sur WhatsApp.
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Numéro de téléphone *
                </label>
                <input
                  type="tel"
                  value={newConvPhone}
                  onChange={(e) => setNewConvPhone(e.target.value)}
                  placeholder="06XXXXXXXX ou +2126XXXXXXXX"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du client (optionnel)
                </label>
                <input
                  type="text"
                  value={newConvName}
                  onChange={(e) => setNewConvName(e.target.value)}
                  placeholder="Ex: Zouhour"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Numéro de commande (optionnel)
                </label>
                <input
                  type="text"
                  value={newConvOrderCode}
                  onChange={(e) => setNewConvOrderCode(e.target.value)}
                  placeholder="Ex: 101744273005"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-5">
              <button
                onClick={() => setShowNewConvModal(false)}
                disabled={sendingNewConv}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleStartNewConversation}
                disabled={sendingNewConv || !newConvPhone.trim()}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {sendingNewConv ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Envoyer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
